import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { presets, type ColorSet } from '@/config/themes';
import { useSettings } from '@/store/settings';
import { useRecords } from '@/store/records';
import { LangSwitch } from '@/ui/LangSwitch';
import { ColorField } from '@/ui/ColorField';
import { RangeMatrix } from '@/ui/RangeMatrix';
import { ComboCards } from '@/ui/ComboCards';
import { exportBackup, importBackup, downloadText } from '@/lib/backup';
import { sfx, SOUND_THEMES, type SoundTheme } from '@/lib/sound';
import { Smile, Zap, Laugh, Wind, ChevronDown } from 'lucide-react';

/** line icons for the sound styles; one stroke width, the bolt a touch larger for equal visual weight */
const THEME_ICON: Record<SoundTheme, React.ReactElement> = {
  calm: <Smile className="size-4" strokeWidth={2} />,
  energetic: <Zap className="size-[18px]" strokeWidth={2} />,
  funny: <Laugh className="size-4" strokeWidth={2} />,
  farts: <Wind className="size-4" strokeWidth={2} />,
};
import { features } from '@/config/features';

const FIELDS: { key: keyof ColorSet; i18n: string; contrast: boolean; tint: boolean }[] = [
  { key: 'a', i18n: 'settings.varA', contrast: true, tint: true },
  { key: 'b', i18n: 'settings.varB', contrast: true, tint: true },
  { key: 'accent', i18n: 'settings.accent', contrast: true, tint: true },
  { key: 'mfill', i18n: 'settings.matrixFill', contrast: true, tint: true },
  { key: 'mcell', i18n: 'settings.matrixCell', contrast: false, tint: false },
  { key: 'combo', i18n: 'settings.comboFill', contrast: false, tint: true },
  { key: 'blocker', i18n: 'settings.blocker', contrast: false, tint: true },
];

type Section = 'language' | 'sound' | 'colors' | 'backup';

/** One collapsible settings section: a label row that toggles its body. */
function Fold({
  id,
  label,
  open,
  onToggle,
  children,
}: {
  id: Section;
  label: string;
  open: boolean;
  onToggle: (id: Section) => void;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col border-t border-line">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={`settings-${id}`}
        onClick={() => onToggle(id)}
        className="flex h-12 items-center justify-between gap-3 text-left transition-colors hover:text-accent"
      >
        <span className="label">{label}</span>
        <ChevronDown
          className={`size-4 text-fg-dim transition-transform ${open ? 'rotate-180' : ''}`}
          strokeWidth={2.4}
          aria-hidden
        />
      </button>
      {/* body stays mounted; the grid row animates 0fr → 1fr so the fold slides open */}
      <div
        id={`settings-${id}`}
        inert={!open}
        className={`grid transition-[grid-template-rows,opacity] duration-(--dur-slow) ease-(--ease) ${open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="flex flex-col gap-2.5 pb-5">{children}</div>
        </div>
      </div>
    </div>
  );
}

export function SettingsScreen({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState<Section | null>('language');
  const toggle = (id: Section): void => setOpen((o) => (o === id ? null : id));
  const colors = useSettings((s) => s.colors);
  const setColor = useSettings((s) => s.setColor);
  const setColors = useSettings((s) => s.setColors);
  const resetSettings = useSettings((s) => s.reset);
  const sound = useSettings((s) => s.sound);
  const setSound = useSettings((s) => s.setSound);
  const soundTheme = useSettings((s) => s.soundTheme);
  const setSoundTheme = useSettings((s) => s.setSoundTheme);
  const glow = useSettings((s) => s.glow);
  const setGlow = useSettings((s) => s.setGlow);
  const resetRecords = useRecords((s) => s.reset);
  const file = useRef<HTMLInputElement>(null);
  const [importError, setImportError] = useState<string | null>(null);

  const onImport = async (f: File | undefined): Promise<void> => {
    if (!f) return;
    setImportError(importBackup(await f.text()));
    if (file.current) file.current.value = '';
  };

  return (
    <section
      className="flex w-full flex-col gap-6 rounded-[20px] border-2 border-accent bg-surface p-8 max-sm:p-6"
      style={{ boxShadow: 'var(--glow)' }}
    >
      <h2
        id="settings-title"
        className="m-0 font-display text-[34px] leading-[1.05] font-black uppercase italic text-accent"
      >
        {t('settings.title')}
      </h2>

      <div className="flex flex-col">
        <Fold
          id="language"
          label={t('settings.language')}
          open={open === 'language'}
          onToggle={toggle}
        >
          <LangSwitch long />
        </Fold>

        {features.sound && (
          <Fold id="sound" label={t('settings.sound')} open={open === 'sound'} onToggle={toggle}>
            <div className="flex flex-wrap items-center gap-3">
              <span
                className="inline-flex gap-[3px] rounded-full border border-line-strong p-[3px]"
                role="group"
                aria-label={t('settings.sound')}
              >
                {[true, false].map((v) => (
                  <button
                    key={String(v)}
                    type="button"
                    aria-pressed={sound === v}
                    onClick={() => {
                      setSound(v);
                      if (v) sfx.correct(soundTheme);
                    }}
                    className="h-[34px] rounded-full px-4 text-[12px] font-bold tracking-[0.12em] text-fg-muted uppercase aria-pressed:bg-accent aria-pressed:text-bg"
                  >
                    {t(v ? 'settings.on' : 'settings.off')}
                  </button>
                ))}
              </span>
              <span
                className="inline-flex gap-[3px] rounded-full border border-line-strong p-[3px]"
                role="group"
                aria-label={t('settings.soundTheme')}
              >
                {SOUND_THEMES.map((th) => (
                  <button
                    key={th}
                    type="button"
                    aria-pressed={soundTheme === th}
                    disabled={!sound}
                    onClick={() => {
                      setSoundTheme(th);
                      sfx.correct(th);
                    }}
                    className="inline-flex h-[34px] items-center gap-1.5 rounded-full px-3.5 text-[12px] font-bold tracking-[0.12em] text-fg-muted uppercase disabled:opacity-40 aria-pressed:bg-accent aria-pressed:text-bg"
                  >
                    <span aria-hidden className="inline-flex">
                      {THEME_ICON[th]}
                    </span>
                    {t(`settings.theme.${th}`)}
                  </button>
                ))}
              </span>
            </div>
          </Fold>
        )}

        <Fold id="colors" label={t('settings.colors')} open={open === 'colors'} onToggle={toggle}>
          <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-3.5">
            {FIELDS.map((f) => (
              <ColorField
                key={f.key}
                label={t(f.i18n)}
                value={colors[f.key]}
                onChange={(v) => setColor(f.key, v)}
                checkContrast={f.contrast}
                tint={f.tint}
              />
            ))}
          </div>
          <div className="flex flex-wrap gap-2.5">
            {presets.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setColors(p)}
                className="inline-flex h-9 items-center gap-2 rounded-full border border-line-strong pr-3.5 pl-3 text-[11px] font-bold tracking-[0.12em] text-fg-muted uppercase hover:border-fg-muted hover:text-fg"
              >
                {[p.accent, p.a, p.b, p.mfill].map((c, i) => (
                  <i
                    key={i}
                    className="inline-block size-3 rounded-full"
                    style={{ background: c }}
                  />
                ))}
                {t(`settings.preset.${p.id}`)}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <span className="label whitespace-nowrap">{t('settings.glow')}</span>
            <span
              className="inline-flex gap-[3px] rounded-full border border-line-strong p-[3px]"
              role="group"
              aria-label={t('settings.glow')}
            >
              {[true, false].map((v) => (
                <button
                  key={String(v)}
                  type="button"
                  aria-pressed={glow === v}
                  onClick={() => setGlow(v)}
                  className="h-[34px] rounded-full px-4 text-[12px] font-bold tracking-[0.12em] text-fg-muted uppercase aria-pressed:bg-accent aria-pressed:text-bg"
                >
                  {t(v ? 'settings.on' : 'settings.off')}
                </button>
              ))}
            </span>
          </div>
          {/* live preview: mini matrix + expression, driven by the same CSS variables the game uses */}
          <div
            className="terms flex flex-wrap items-center justify-center gap-6 rounded-[14px] border border-dashed border-line-strong bg-bg p-4"
            style={{ '--cell': '12px' } as React.CSSProperties}
          >
            <div className="flex items-center gap-3">
              <ComboCards a={12} side="both" blockedRows={[0]} />
              <RangeMatrix b={42} />
            </div>
            <span className="num inline-flex items-center gap-[0.22em] font-display text-[clamp(28px,5vw,44px)] leading-none font-black">
              <span className="va">12</span>
              <span className="text-[0.7em] text-fg-dim">×</span>
              <span className="vb">42</span>
              <span className="mx-2 text-fg-muted">−</span>
              <span className="va">9</span>
              <span className="text-[0.7em] text-fg-dim">×</span>
              <span className="vb">7</span>
              <span className="ml-2 text-fg-muted">=</span>
            </span>
          </div>
        </Fold>

        <Fold id="backup" label={t('settings.backup')} open={open === 'backup'} onToggle={toggle}>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => downloadText('cardcounter-backup.json', exportBackup())}
              className="h-9 rounded-full border border-line-strong px-4 text-[11px] font-bold tracking-[0.12em] text-fg-muted uppercase hover:border-accent hover:text-accent"
            >
              {t('settings.export')}
            </button>
            <button
              type="button"
              onClick={() => file.current?.click()}
              className="h-9 rounded-full border border-line-strong px-4 text-[11px] font-bold tracking-[0.12em] text-fg-muted uppercase hover:border-accent hover:text-accent"
            >
              {t('settings.import')}
            </button>
            <input
              ref={file}
              type="file"
              accept="application/json"
              hidden
              onChange={(e) => void onImport(e.target.files?.[0])}
            />
            {importError && (
              <span className="text-[11px] font-bold text-err">
                {t('settings.importError')}: {importError}
              </span>
            )}
          </div>
        </Fold>
      </div>

      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          onClick={onClose}
          className="inline-flex h-11 items-center rounded-full bg-accent px-[22px] text-[12px] font-bold tracking-[0.16em] text-bg uppercase"
        >
          {t('common.done')}
        </button>
        <button
          type="button"
          onClick={() => {
            resetSettings();
            resetRecords();
          }}
          className="h-11 rounded-full border-2 border-line-strong px-[22px] text-[12px] font-bold tracking-[0.16em] text-fg-muted uppercase hover:border-accent hover:text-accent"
        >
          {t('settings.reset')}
        </button>
      </div>
    </section>
  );
}
